import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding,
  Inject, Input, OnDestroy, OnInit, Output, PLATFORM_ID, QueryList, Renderer2, ViewChild, ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MediaMatcher } from '@angular/cdk/layout';

import { fromEvent, Subscription } from 'rxjs';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import SwiperCore, { Navigation, Pagination, SwiperOptions, Thumbs, Scrollbar, Mousewheel } from 'swiper';
import { SwiperComponent, SwiperModule } from 'swiper/angular';

import {
  BrandsEnum, DeviceService, ImageRatioEnum, IMedia, IMediaGallery, mBreakpoint, SdBannerComponent,
  SdButtonComponent, SdModalDefaultComponent, SdSkeletonComponent, SkeletonAppearanceEnum
} from '@seb/web-design-system/core';

import { SliderUtils } from '../slider-a11y.utils';
import { SdProductLogoComponent } from '@seb/web-design-system/product-logo/src/product-logo.component';

SwiperCore.use([Navigation, Pagination, Thumbs, Scrollbar, Mousewheel]);
@Component({
  selector: 'sd-gallery',
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    SdSkeletonComponent,
    SwiperModule,
    SdBannerComponent,
    SdProductLogoComponent,
    SdButtonComponent,
    SdModalDefaultComponent,
    TranslateModule
  ]
})
export class SdGalleryComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input()
  brand: BrandsEnum;

  @Input()
  isLoading = false;

  @ViewChild('zoomResult', { read: ElementRef, static: false })
  zoomResult: ElementRef;

  @ViewChild('zoomBackground', { read: ElementRef, static: false })
  zoomBackground: ElementRef;

  @ViewChild('gallerySwiper', { static: false })
  gallerySwiper?: SwiperComponent;

  @ViewChild('gallerySwiper', { read: ElementRef, static: false })
  galleryElement?: ElementRef;

  @ViewChild('gallerySwiperModal', { static: false })
  gallerySwiperModal?: SwiperComponent;

  @ViewChild('defaultSwiperModalThumbs', { static: false })
  defaultSwiperModalThumbs?: SwiperComponent;

  @ViewChildren('mediaBanner')
  mediaBanners: QueryList<SdBannerComponent>;

  @ViewChildren('thumbMediaBanner')
  thumbMediaBanners: QueryList<SdBannerComponent>;

  @ViewChild('gallerySwiperModal', { static: false, read: ElementRef })
  zoomContainer: ElementRef;

  @Input()
  hasBorder = false;

  @Input()
  class: string; // override the standard class attr with a new one.
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.hasBorder && !this.isMobile ? 'has-border' : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  @Input()
  get images(): IMediaGallery[] {
    return this._images;
  }
  set images(images: IMediaGallery[]) {
    this._images = images;

    if (this._images) {
      this._images.sort((a, b) => a.original.galleryIndex - b.original.galleryIndex);
      this.originalImages = this._images.map((image) => image?.original);
      this.mainImages = this._images.map((image) => image?.main);
      this.extraImages = this._images.map((image) => image?.extra);
      this.thumbnails = this._images.map((image) => image?.thumbnail);
    }

    this.hasExtraImage =
      !!this.extraImages[0] &&
      (this.extraImages[0]?.url?.length > 0 ||
        this.extraImages[0]?.urlTwicPic?.length > 0);
  }

  @Input()
  idVideo = 'video';

  @Input()
  context = 'Gallery';

  @Input()
  galleryName: string;

  @Input()
  madeInData: IMedia;

  @Input()
  refurbishedData: IMedia;

  @Output()
  videoPlayed = new EventEmitter<{ status: string; title: string }>();

  @Output()
  videoEnded = new EventEmitter<{ status: string; title: string }>();

  SkeletonAppearanceEnum = SkeletonAppearanceEnum;
  ImageRatioEnum = ImageRatioEnum;

  subscriptions = new Subscription();
  twicBackground: string;
  isMobile: boolean;
  breakpoint: MediaQueryList;
  _images: IMediaGallery[];
  originalImages: IMedia[] = [];
  extraImages: IMedia[] = [];
  mainImages: IMedia[] = [];
  thumbnails: IMedia[] = [];
  zoom = false;
  isVideo: boolean;
  hasExtraImage: boolean;
  isOpen = false;
  currIndex = 0;
  maxSlide: number;
  sliderUtils: SliderUtils;
  isInit = true;
  shouldRenderModal = false;
  /**
   * Zoom Variables
   */
  isZoomed = false;
  zoomLevel = 0;
  isImageDragging = false;
  initialMouseX = 0;
  initialMouseY = 0;
  initialImageX = 0;
  initialImageY = 0;
  clickTimer;

  configGallery: SwiperOptions;
  configGalleryModal: SwiperOptions;
  configDefaultModalThumbs: SwiperOptions;

  imageSelector = '.c__items .c__item.swiper-slide-active img';

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private cdRef: ChangeDetectorRef,
    private renderer: Renderer2,
    public mediaMatcher: MediaMatcher,
    private deviceService: DeviceService,
    private translateService: TranslateService
  ) {
    this.sliderUtils = new SliderUtils(
      this.renderer,
      this.cdRef,
      this.translateService,
      this.subscriptions
    );
  }

  ngOnInit() {
    this.subscriptions.add(
      this.deviceService.isMobile.subscribe((x) => {
        this.isMobile = x;
        this.cdRef.markForCheck();
      })
    );

    if (isPlatformBrowser(this.platformId)) {
      this.shouldRenderModal = true;
      this.breakpoint = this.mediaMatcher.matchMedia(
        '(max-width: ' + (mBreakpoint - 1) + 'px)',
      );
      this.subscriptions.add(
        fromEvent(this.breakpoint, 'change').subscribe(() => {
          this.updateSlidesStatus();
          this.cdRef.markForCheck();
        })
      );
    }
    this.manageConfig();
    this.cdRef.markForCheck();
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      const gallerySwiperEl = this.gallerySwiper?.swiperRef;
      if (gallerySwiperEl) {
        gallerySwiperEl.changeDirection('horizontal');
        this.maxSlide = gallerySwiperEl.slides.length;
      }

      /**
       * A11y
       */
      this.sliderUtils.setSliderA11y(
        this.context,
        this.mediaBanners,
        this.thumbMediaBanners
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.gallerySwiper?.swiperRef?.destroy();
    this.gallerySwiperModal?.swiperRef?.destroy();
  }

  manageConfig() {
    this.configGallery = {
      slidesPerView: 1,
      direction: 'horizontal',
      watchSlidesProgress: true,
      breakpoints: {
        0: {
          simulateTouch: true
        },
        768: {
          simulateTouch: false,
          pagination: false,
          direction: 'horizontal',
          zoom: false
        },
        1280: {
          pagination: false,
          direction: 'horizontal'
        }
      },
      pagination: {
        dynamicBullets: false,
        type: 'bullets',
        clickable: true
      },
      loop: false,
      rewind: true
    };

    this.configGalleryModal = {
      slidesPerView: 1,
      watchSlidesProgress: true,
      navigation: {
        enabled: true,
        nextEl: '.c__swiper-button-next-gallery',
        prevEl: '.c__swiper-button-prev-gallery',
        disabledClass: 'is-disabled'
      },
      wrapperClass: 'c__items',
      slideClass: 'c__item',
      direction: 'horizontal',
      allowTouchMove: false,
      loop: false,
      rewind: true
    } as SwiperOptions;

    this.configDefaultModalThumbs = {
      zoom: false,
      cssMode: false,
      direction: this.isMobile ? 'horizontal' : 'vertical',
      observer: true,
      spaceBetween: 8,
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      loop: false,
      speed: 600,
      watchOverflow: true,
      watchSlidesProgress: true,
      a11y: {
        firstSlideMessage: 'First slide',
        lastSlideMessage: 'Last slide'
      },
      breakpoints: {
        0: {
          direction: 'horizontal',
          slidesPerView: 4,
          slidesPerGroup: 1,
          scrollbar: {
            hide: true
          }
        },
        768: {
          direction: 'vertical',
          slidesPerView: 6,
          scrollbar: {
            hide: false
          }
        }
      },
      scrollbar: { draggable: true },
      mousewheel: true
    };
  }

  /**
   * Toogle zoom functionality
   */

  zoomImage() {
    if (!this.isZoomed) {
      this.zoomLevel = 1;
      this.isZoomed = true;
    } else {
      this.zoomLevel++;
      if (this.zoomLevel > 2) {
        this.zoomLevel = 0;
        this.isZoomed = false;
      }
    }
  }

  zoomOut() {
    if (isPlatformBrowser(this.platformId)) {
      if (this.isZoomed) {
        this.zoomLevel--;
        if (this.zoomLevel === 0) {
          this.isZoomed = false;
        }
        const img = this.zoomContainer?.nativeElement.querySelector(
          this.imageSelector,
        );
        if (img) {
          img.style.transform = 'translate(-50%, -50%)';
        }
      }
    }
  }

  /**
   * Zoom functionality
   */

  onImageDown(event: MouseEvent | TouchEvent) {
    if (isPlatformBrowser(this.platformId)) {

      if (this.mainImages?.[this.currIndex]?.isVideo) {
        if (this.clickTimer) {
          clearTimeout(this.clickTimer);
          this.clickTimer = undefined;
        }
        return;
      }
      event.preventDefault();
      this.isImageDragging = true;

      if (event instanceof MouseEvent) {
        this.initialMouseX = event.clientX;
        this.initialMouseY = event.clientY;
      } else if (event instanceof TouchEvent) {
        const touch = event.touches[0];
        this.initialMouseX = touch.clientX;
        this.initialMouseY = touch.clientY;
      }
      const img = this.zoomContainer?.nativeElement.querySelector(
        this.imageSelector,
      );
      if (img) {
        const transformMatrix = getComputedStyle(img).transform;
        const matrix = new DOMMatrix(transformMatrix);
        this.initialImageX = matrix.e;
        this.initialImageY = matrix.f;
      }

      this.clickTimer = setTimeout(() => {
        this.clickTimer = undefined;
      }, 230);
    }
  }

  onImageMove(event: MouseEvent) {
    if (isPlatformBrowser(this.platformId)) {
      if (this.isImageDragging && this.zoomLevel > 0) {
        const deltaX = event.clientX - this.initialMouseX;
        const deltaY = event.clientY - this.initialMouseY;
        const newImageX = this.initialImageX + deltaX;
        const newImageY = this.initialImageY + deltaY;
        const img = this.zoomContainer?.nativeElement.querySelector(
          this.imageSelector,
        );
        if (img) {
          img.style.transform = `translate(${newImageX}px, ${newImageY}px)`;
        }
      }
    }
  }

  onImageTouchMove(event: TouchEvent) {
    if (isPlatformBrowser(this.platformId)) {
      if (this.isImageDragging && this.zoomLevel > 0) {
        const touch = event.touches[0];
        const deltaX = touch.clientX - this.initialMouseX;
        const deltaY = touch.clientY - this.initialMouseY;
        const newImageX = this.initialImageX + deltaX;
        const newImageY = this.initialImageY + deltaY;
        const img = this.zoomContainer?.nativeElement.querySelector(
          this.imageSelector,
        );
        if (img) {
          img.style.transform = `translate(${newImageX}px, ${newImageY}px)`;
        }
      }
    }
  }

  onImageUp() {
    if (isPlatformBrowser(this.platformId)) {
      this.isImageDragging = false;

      if (this.clickTimer) {
        clearTimeout(this.clickTimer);
        this.clickTimer = undefined;
        this.zoomImage();
        const img = this.zoomContainer?.nativeElement.querySelector(
          this.imageSelector,
        );
        if (img && this.zoomLevel === 0) {
          img.style.transform = 'translate(-50%, -50%)';
        }
      }
    }
  }

  /**
   * Init Modal Gallery when modal is open
   */
  toggleModal(index?: number) {
    if (isPlatformBrowser(this.platformId)) {
      this.currIndex = index;

      if (!this.mainImages[this.currIndex]?.isVideo) {
        this.isOpen = !this.isOpen;

        /**
         * Init Modal Swiper
         */
        if (this.gallerySwiperModal) {
          const gallerySwiperModalEl = this.gallerySwiperModal.swiperRef;
          if (gallerySwiperModalEl) {
            window.setTimeout(() => {
              gallerySwiperModalEl.init();
              gallerySwiperModalEl.update();
            });
          }
        }
        if (this.defaultSwiperModalThumbs) {
          const defaultSwiperModalThumbsEl =
            this.defaultSwiperModalThumbs.swiperRef;

          if (
            defaultSwiperModalThumbsEl &&
            !defaultSwiperModalThumbsEl.destroyed
          ) {
            window.setTimeout(() => {
              defaultSwiperModalThumbsEl.init();
              defaultSwiperModalThumbsEl.update();
              defaultSwiperModalThumbsEl.slideTo(0);
            });
          }
        }

        this.goToSlide(this.currIndex);
      }
    }
  }

  goToSlide(index: number) {
    if (isPlatformBrowser(this.platformId)) {
      const galleryModal = this.gallerySwiperModal?.swiperRef;
      const gallery = this.gallerySwiper?.swiperRef;
      if (index !== undefined) {
        gallery?.slideToLoop(index);
        galleryModal?.slideToLoop(index);
      }
    }
  }

  slideNext() {
    if (isPlatformBrowser(this.platformId)) {
      const gallerySwiperModal = this.gallerySwiperModal?.swiperRef;
      gallerySwiperModal.slideNext();
    }
  }

  slidePrev() {
    if (isPlatformBrowser(this.platformId)) {
      const gallerySwiperModal = this.gallerySwiperModal?.swiperRef;
      gallerySwiperModal.slidePrev();
    }
  }

  /**
   * Force focus on active slide
   * (will probably need rework after a11Y feedback)
   */
  updateSlidesStatus() {
    if (isPlatformBrowser(this.platformId)) {
      this.isVideo =
        this.mainImages[this.gallerySwiper?.swiperRef?.realIndex]?.isVideo;
      this.hasExtraImage =
        !!this.extraImages[this.gallerySwiper?.swiperRef?.realIndex] &&
        (this.extraImages[this.gallerySwiper?.swiperRef?.realIndex]?.url?.length > 0 ||
          this.extraImages[this.gallerySwiper?.swiperRef?.realIndex]?.urlTwicPic?.length > 0);
      setTimeout(() => {
        this.sliderUtils.updateSlides(
          this.galleryElement,
          this.mediaBanners,
          this.gallerySwiper?.swiperRef
        );
        this.isInit = false;
      });
    }
  }

  /**
   * get the index of current slide and max slide (for custom pagination)
   */
  updatePagination(event, swiperName: string) {
    if (isPlatformBrowser(this.platformId)) {
      this.isZoomed = false;
      this.zoomLevel = 0;
      const img = this.zoomContainer?.nativeElement.querySelector(
        this.imageSelector,
      );
      if (img) {
        img.style.transform = '';
      }
      if (
        swiperName === 'gallery' &&
        this.gallerySwiper &&
        this.gallerySwiper.swiperRef
      ) {
        this.currIndex = event[0]?.realIndex;
      } else if (
        swiperName === 'galleryModal' &&
        this.gallerySwiperModal &&
        this.gallerySwiperModal.swiperRef &&
        this.isOpen
      ) {
        this.currIndex = event[0]?.realIndex;
      }
    }
  }

  onVideoPlayed() {
    this.videoPlayed.emit({ status: 'play', title: this.galleryName });
  }

  onVideoEnded() {
    this.videoEnded.emit({ status: 'ended', title: this.galleryName });
  }
}
